<section aria-labelledby="tob-heading">
  <h2 id="tob-heading">Create Service Changes & Transfers Ticket</h2>

  @if (tobTxGroup) {
    <form
      [formGroup]="tobTxGroup"
      [ngClass]="{ 'form-submit-attempt': formSubmitAttempted() }"
      autocomplete="off"
    >
      <app-primary-reporter
        [formRef]="tobTxGroup"
        [reporters]="primaryReporters"
        [altContact]="false"
        (usersLoaded)="usersLoaded()"
      >
      </app-primary-reporter>

      <div class="mt-6">
        <div id="request-type-label" class="comcast-input-label">
          What would you like to do?
          <span class="comcast-required-marker" aria-hidden="true">*</span>
        </div>
        <div
          class="mt-3 grid grid-cols-2 gap-3"
          role="radiogroup"
          aria-labelledby="request-type-label"
        >
          <button
            type="button"
            class="comcast-radio-card min-h-24"
            [ngClass]="
              tobTxGroup.value.requestType === CHANGE_REQUEST
                ? 'comcast-radio-card--selected'
                : 'comcast-radio-card--idle'
            "
            role="radio"
            [attr.aria-checked]="
              tobTxGroup.value.requestType === CHANGE_REQUEST
            "
            (click)="
              tobTxGroup.controls.requestType.setValue(CHANGE_REQUEST);
              onRequestTypeChange()
            "
          >
            <span
              class="font-lato text-base font-bold"
              [ngClass]="
                tobTxGroup.value.requestType === CHANGE_REQUEST
                  ? 'text-blue-700'
                  : 'text-black'
              "
            >
              Change or upgrade services
            </span>
            <span
              class="font-lato mt-1 text-sm"
              [ngClass]="
                tobTxGroup.value.requestType === CHANGE_REQUEST
                  ? 'text-blue-700'
                  : 'text-brand-muted'
              "
            >
              Request a service alteration, upgrade, or related change.
            </span>
          </button>

          <button
            type="button"
            class="comcast-radio-card min-h-24"
            [ngClass]="
              tobTxGroup.value.requestType === TRANSFER_REQUEST
                ? 'comcast-radio-card--selected'
                : 'comcast-radio-card--idle'
            "
            role="radio"
            [attr.aria-checked]="
              tobTxGroup.value.requestType === TRANSFER_REQUEST
            "
            (click)="
              tobTxGroup.controls.requestType.setValue(TRANSFER_REQUEST);
              onRequestTypeChange()
            "
          >
            <span
              class="font-lato text-base font-bold"
              [ngClass]="
                tobTxGroup.value.requestType === TRANSFER_REQUEST
                  ? 'text-blue-700'
                  : 'text-black'
              "
            >
              Transfer services
            </span>
            <span
              class="font-lato mt-1 text-sm"
              [ngClass]="
                tobTxGroup.value.requestType === TRANSFER_REQUEST
                  ? 'text-blue-700'
                  : 'text-brand-muted'
              "
            >
              Move selected services to a new account.
            </span>
          </button>
        </div>
      </div>

      @if (isTransferRequest) {
        <div class="mt-4">
          <label class="comcast-input-label" for="new-account-name">
            New account name
            <span class="comcast-required-marker" aria-hidden="true">*</span>
          </label>
          <input
            id="new-account-name"
            class="comcast-input"
            [ngClass]="{
              'comcast-input-invalid': shouldShowError('newAccountName'),
            }"
            type="text"
            formControlName="newAccountName"
            placeholder="Account name"
            aria-required="true"
            [attr.aria-invalid]="shouldShowError('newAccountName')"
            [attr.aria-describedby]="
              shouldShowError('newAccountName')
                ? 'new-account-name-error'
                : null
            "
            (keyup)="canSubmit()"
          />
          @if (shouldShowError('newAccountName')) {
            <div
              id="new-account-name-error"
              class="comcast-validation-error"
              role="alert"
            >
              {{ inlineErrors.newAccountName }}
            </div>
          }
        </div>

        <div class="mt-4">
          <label class="comcast-input-label" for="new-fein">
            New FEIN#
            <span class="comcast-required-marker" aria-hidden="true">*</span>
          </label>
          <input
            id="new-fein"
            class="comcast-input"
            [ngClass]="{ 'comcast-input-invalid': shouldShowError('newFEIN') }"
            type="text"
            formControlName="newFEIN"
            placeholder="FEIN #"
            aria-required="true"
            [attr.aria-invalid]="shouldShowError('newFEIN')"
            [attr.aria-describedby]="
              shouldShowError('newFEIN') ? 'new-fein-error' : null
            "
            (keyup)="canSubmit()"
          />
          @if (shouldShowError('newFEIN')) {
            <div
              id="new-fein-error"
              class="comcast-validation-error"
              role="alert"
            >
              {{ inlineErrors.newFEIN }}
            </div>
          }
        </div>

        <div
          class="mt-4"
          formGroupName="newAddress"
          role="group"
          aria-labelledby="billing-address-label"
        >
          <div id="billing-address-label" class="comcast-input-label">
            New billing address
          </div>
          <div class="mt-2">
            <input
              id="street1-input"
              class="comcast-input !mt-0"
              [ngClass]="{
                'comcast-input-invalid': shouldShowError('newAddress.street1'),
              }"
              type="text"
              formControlName="street1"
              placeholder="Street*"
              aria-label="Street address"
              aria-required="true"
              [attr.aria-invalid]="shouldShowError('newAddress.street1')"
              [attr.aria-describedby]="
                shouldShowError('newAddress.street1') ? 'street1-error' : null
              "
              (keyup)="canSubmit()"
            />
            @if (shouldShowError('newAddress.street1')) {
              <div
                id="street1-error"
                class="comcast-validation-error"
                role="alert"
              >
                {{ inlineErrors['newAddress.street1'] }}
              </div>
            }
          </div>

          <div class="mt-3">
            <input
              class="comcast-input !mt-0"
              type="text"
              formControlName="street2"
              placeholder="Apt, Suite, Floor (optional)"
              aria-label="Apartment, suite, or floor (optional)"
            />
          </div>

          <div
            class="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1fr_8rem_10rem]"
          >
            <div>
              <input
                id="city-input"
                class="comcast-input !mt-0"
                [ngClass]="{
                  'comcast-input-invalid': shouldShowError('newAddress.city'),
                }"
                type="text"
                formControlName="city"
                placeholder="City*"
                aria-label="City"
                aria-required="true"
                [attr.aria-invalid]="shouldShowError('newAddress.city')"
                [attr.aria-describedby]="
                  shouldShowError('newAddress.city') ? 'city-error' : null
                "
                (keyup)="canSubmit()"
              />
              @if (shouldShowError('newAddress.city')) {
                <div
                  id="city-error"
                  class="comcast-validation-error"
                  role="alert"
                >
                  {{ inlineErrors['newAddress.city'] }}
                </div>
              }
            </div>

            <div>
              <input
                id="state-input"
                class="comcast-input !mt-0"
                [ngClass]="{
                  'comcast-input-invalid': shouldShowError('newAddress.state'),
                }"
                type="text"
                formControlName="state"
                placeholder="State*"
                aria-label="State"
                aria-required="true"
                [attr.aria-invalid]="shouldShowError('newAddress.state')"
                [attr.aria-describedby]="
                  shouldShowError('newAddress.state') ? 'state-error' : null
                "
                (keyup)="canSubmit()"
              />
              @if (shouldShowError('newAddress.state')) {
                <div
                  id="state-error"
                  class="comcast-validation-error"
                  role="alert"
                >
                  {{ inlineErrors['newAddress.state'] }}
                </div>
              }
            </div>

            <div>
              <input
                id="zip-input"
                class="comcast-input !mt-0"
                [ngClass]="{
                  'comcast-input-invalid': shouldShowError('newAddress.zip'),
                }"
                type="text"
                formControlName="zip"
                placeholder="Zip Code*"
                aria-label="Zip code"
                aria-required="true"
                [attr.aria-invalid]="shouldShowError('newAddress.zip')"
                [attr.aria-describedby]="
                  shouldShowError('newAddress.zip') ? 'zip-error' : null
                "
                (keyup)="canSubmit()"
              />
              @if (shouldShowError('newAddress.zip')) {
                <div
                  id="zip-error"
                  class="comcast-validation-error"
                  role="alert"
                >
                  {{ inlineErrors['newAddress.zip'] }}
                </div>
              }
            </div>
          </div>
        </div>

        <label class="comcast-input-label mt-4" for="desired-date-input">
          Desired transfer date
          <span class="comcast-required-marker" aria-hidden="true">*</span>
        </label>
        <div class="relative mt-2">
          <input
            id="desired-date-input"
            class="comcast-input !mt-0 pr-14"
            [ngClass]="{
              'comcast-input-invalid': shouldShowError('desiredDate'),
            }"
            formControlName="desiredDate"
            (dateChange)="canSubmit()"
            [matDatepickerFilter]="filterDates"
            [matDatepicker]="picker"
            placeholder="Please select a date"
            aria-required="true"
            [attr.aria-invalid]="shouldShowError('desiredDate')"
            [attr.aria-describedby]="
              shouldShowError('desiredDate') ? 'desired-date-error' : null
            "
            (keyup)="canSubmit()"
            (click)="picker.open()"
          />
          <button
            type="button"
            class="text-brand-text absolute top-1/2 right-4 flex h-8 w-8 -translate-y-1/2 items-center justify-center border-0 bg-transparent"
            (click)="picker.open()"
            aria-label="Open calendar"
          >
            <mat-icon fontSet="material-icons-outlined" aria-hidden="true"
              >calendar_month</mat-icon
            >
          </button>
          <mat-datepicker
            #picker
            [calendarHeaderComponent]="customCalHeader"
            [startAt]="startCalendarAt()"
          ></mat-datepicker>
        </div>
        @if (shouldShowError('desiredDate')) {
          <div
            id="desired-date-error"
            class="comcast-validation-error"
            role="alert"
          >
            {{ inlineErrors.desiredDate }}
          </div>
        }

        <app-select-multiple-services
          [mode]="ServiceSelectMode.Transfer"
          [formRef]="tobTxGroup"
          [inlineErrors]="inlineErrors"
          (servicesLoaded)="servicesLoaded()"
          [selectedServiceId]="selectedServiceId"
          (keyup)="canSubmit()"
        >
        </app-select-multiple-services>
      }

      @if (isChangeRequest) {
        <app-select-single-service
          [formRef]="tobTxGroup"
          [services]="services"
          [selectedServiceId]="selectedServiceId"
          [inlineErrors]="inlineErrors"
          (servicesLoaded)="servicesLoaded()"
        ></app-select-single-service>

        <div class="mt-6">
          <label class="comcast-input-label" for="tob-subject">
            Subject
            <span class="comcast-required-marker" aria-hidden="true">*</span>
          </label>
          <input
            id="tob-subject"
            type="text"
            formControlName="subject"
            class="comcast-input mt-2"
            [ngClass]="{
              'comcast-input-invalid': shouldShowError('subject'),
            }"
            placeholder="Tell us what you want to change..."
            aria-required="true"
            [attr.aria-invalid]="shouldShowError('subject')"
            [attr.aria-describedby]="
              shouldShowError('subject') ? 'tob-subject-error' : null
            "
            (keyup)="canSubmit()"
          />
          @if (shouldShowError('subject')) {
            <div
              id="tob-subject-error"
              class="comcast-validation-error"
              role="alert"
            >
              {{ inlineErrors.subject }}
            </div>
          }
        </div>

        <div class="mt-6">
          <label class="comcast-input-label" for="tob-description">
            Please describe the service change or upgrade
            <span class="comcast-required-marker" aria-hidden="true">*</span>
          </label>
          <textarea
            id="tob-description"
            formControlName="description"
            class="comcast-textarea"
            [ngClass]="{
              'comcast-input-invalid': shouldShowError('description'),
            }"
            rows="5"
            maxlength="2000"
            placeholder="Tell us about the change you need..."
            aria-required="true"
            [attr.aria-invalid]="shouldShowError('description')"
            [attr.aria-describedby]="
              shouldShowError('description')
                ? 'tob-description-error tob-description-count'
                : 'tob-description-count'
            "
            (keyup)="canSubmit()"
          ></textarea>
          <div class="mt-1 flex items-center justify-between">
            @if (shouldShowError('description')) {
              <div
                id="tob-description-error"
                class="comcast-validation-error !mt-0"
                role="alert"
              >
                {{ inlineErrors.description }}
              </div>
            } @else {
              <div></div>
            }
            <div
              id="tob-description-count"
              class="comcast-textarea-count !mt-0"
              aria-live="polite"
              aria-atomic="true"
            >
              {{ tobTxGroup.get('description').value?.length || 0 }} / 2000
            </div>
          </div>
        </div>
      }

      @if (isTransferRequest) {
        <div class="mt-4">
          <label class="comcast-input-label" for="additional-comment"
            >Additional comments</label
          >
          <textarea
            id="additional-comment"
            class="comcast-textarea"
            formControlName="additionalComment"
            rows="5"
            maxlength="400"
            placeholder="Add any details to help us process your request..."
            aria-describedby="additional-comment-count"
          ></textarea>
          <div
            id="additional-comment-count"
            class="comcast-textarea-count"
            aria-live="polite"
            aria-atomic="true"
          >
            {{ tobTxGroup.get('additionalComment').value?.length || 0 }} / 400
          </div>
        </div>
      }

      @if (submissionError) {
        <div
          class="comcast-error-alert mt-4"
          role="alert"
          aria-live="assertive"
        >
          <mat-icon
            fontSet="material-icons-outlined"
            class="comcast-error-alert-icon"
            aria-hidden="true"
            >warning</mat-icon
          >
          <span class="comcast-error-alert-text">
            {{
              errorMessage || 'Unable to submit the ticket. Please try again!'
            }}
          </span>
        </div>
      }

      <div class="mt-6">
        <button
          class="btn-primary w-full"
          (click)="submitTicket()"
          [disabled]="areServicesLoading() || areUsersLoading() || isSubmitting"
          [attr.aria-busy]="isSubmitting"
          [attr.aria-label]="
            isSubmitting ? 'Submitting ticket, please wait' : 'Submit ticket'
          "
        >
          @if (isSubmitting) {
            <mat-spinner
              [diameter]="20"
              color="accent"
              aria-hidden="true"
            ></mat-spinner>
          } @else {
            Submit ticket
          }
        </button>
      </div>
    </form>
  }
</section>
